import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  ActionActivityService,
  ActionFeedbackService,
} from '@arm-web/ui-components';

/**
 * How long a route change has to run before it is reported at all.
 *
 * Most navigation inside the application is a repaint: the chunk is already
 * in memory and the new screen is there within a frame. Reporting that would
 * flash a spinner on the control that was clicked, which reads as a glitch.
 * Only a navigation the user can actually perceive is worth showing.
 */
const NAVIGATION_GRACE_MS = 150;

/**
 * Counts a route change as application activity, so moving between screens
 * gets the same progress indicator as a request does.
 *
 * A navigation is adoptable: the control that was clicked to start it keeps
 * its loading state until the new screen is ready, the same way it would for
 * a request. Most buttons in the application open another screen rather than
 * post something, and they used to give the user nothing back.
 */
@Injectable({ providedIn: 'root' })
export class NavigationActivityService {
  private readonly activity = inject(ActionActivityService);
  private readonly feedback = inject(ActionFeedbackService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private handle: number | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private tracking = false;

  /** Called once from the root component. */
  track(): void {
    if (this.tracking) {
      return;
    }
    this.tracking = true;

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: Event) => this.onRouterEvent(event));
  }

  private onRouterEvent(event: Event): void {
    if (event instanceof NavigationStart) {
      // A redirect can start a navigation before the previous one settles.
      this.settle();
      // Still inside the click's adoption window, so the control that was
      // clicked picks this up once the navigation proves slow enough to show.
      this.graceTimer = setTimeout(() => {
        this.graceTimer = null;
        this.handle = this.activity.start();
      }, NAVIGATION_GRACE_MS);
      return;
    }

    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.settle();
      // The screen the clicked control lived on may be gone; anything left
      // spinning on the old page has no ending coming.
      this.feedback.releaseAll();
    }
  }

  private settle(): void {
    if (this.graceTimer !== null) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }

    if (this.handle === null) {
      return;
    }

    this.activity.finish(this.handle);
    this.handle = null;
  }
}
